import type {
  OfflineAdjustmentOperation,
  OfflineAdjustmentPayload,
  OfflineOrderPayload,
} from "../../../domain/contracts/offline-sync";
import { localOrderHasReward } from "./clear-local-order-reward";
import type { LocalOrderOperationRecord, LocalOrderRecord } from "./types";

export function buildSyncPayloadFromLocalOrder(
  order: LocalOrderRecord,
): OfflineOrderPayload {
  const capturedAt = order.completedAt ?? order.updatedAt ?? order.createdAt;
  const includeReward = localOrderHasReward(order);

  return {
    protocolVersion: 1,
    operation: "CHECKOUT_AND_COMPLETE",
    clientOrderId: order.clientOrderId,
    idempotencyKey: order.idempotencyKey,
    capturedAt,
    completedShiftId: order.completedShiftId,
    request: {
      cartItems: order.cartItems,
      selection: order.selection,
      isDelivery: order.isDelivery,
      deliveryData: order.deliveryData,
      customerId: order.customerId,
      customerSnapshot: order.customerSnapshot,
      couponCode: order.couponCode,
      couponApplyToCartIndices: order.couponApplyToCartIndices,
      couponApplications: order.couponApplications,
      couponSnapshots: order.couponSnapshots,
      voucherSnapshots: order.voucherSnapshots,
      discountBreakdown: order.discountBreakdown,
      pricingSource: order.pricingSource,
      payments: order.payments,
      skipKitchenValidation: true,
      voucherCodes: order.voucherCodes,
      paymentCouponCode: order.paymentCouponCode,
      couponOrderItemIds: order.couponOrderItemIds,
      paymentCouponCartIndices: order.paymentCouponCartIndices,
      paymentCouponApplications: order.paymentCouponApplications,
      rewardSnapshot: includeReward ? order.rewardSnapshot : undefined,
      rewardId: includeReward
        ? (order.rewardId ?? order.rewardSnapshot?.rewardId)
        : undefined,
    },
  };
}

const ADJUSTMENT_OPERATION_BY_LOCAL_TYPE: Partial<
  Record<LocalOrderOperationRecord["type"], OfflineAdjustmentOperation>
> = {
  void_order: "VOID_ORDER",
  refund_order: "REFUND_ORDER",
  move_table: "MOVE_TABLE",
  merge_orders: "MERGE_ORDERS",
};

function normalizeAdjustmentPayload(payload: unknown): Record<string, unknown> {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }
  return {};
}

export function buildAdjustmentSyncPayload(
  operation: LocalOrderOperationRecord,
  order?: LocalOrderRecord,
): OfflineAdjustmentPayload {
  const adjustmentOperation =
    ADJUSTMENT_OPERATION_BY_LOCAL_TYPE[operation.type];
  if (!adjustmentOperation) {
    throw new Error(`Unsupported adjustment operation: ${operation.type}`);
  }

  return {
    protocolVersion: 1,
    operation: adjustmentOperation,
    operationId: operation.operationId,
    clientOrderId: operation.clientOrderId,
    idempotencyKey: `${operation.branchId}:${operation.deviceId}:${operation.operationId}`,
    capturedAt: operation.capturedAt ?? operation.createdAt,
    serverOrderId: order?.serverOrderId,
    performedBy: operation.performedBy,
    payload: normalizeAdjustmentPayload(operation.payload),
  };
}
